import type { VerbatimRespondent } from '../data/verbatimData';

export interface ThemeResult {
  theme: string;
  label: string;
  count: number;
  percentage: number;
  sampleQuotes: string[];
}

type ThemeField = 'goals' | 'wtpFactors' | 'brandPerception';

const THEME_KEYWORDS: Record<ThemeField, Record<string, { label: string; keywords: string[] }>> = {
  goals: {
    weight_loss:      { label: 'Weight Loss / Body Composition', keywords: ['weight', 'lose', 'pounds', 'fat', 'slim', 'lean', 'skinny', 'obesity', 'bmi'] },
    muscle_strength:  { label: 'Muscle Building / Strength',     keywords: ['muscle', 'strength', 'strong', 'tone', 'build', 'lift', 'bulk'] },
    general_fitness:  { label: 'General Fitness / Activity',      keywords: ['fitness', 'active', 'exercise', 'move', 'energy', 'endurance', 'cardio', 'stamina'] },
    specific_health:  { label: 'Specific Health Conditions',      keywords: ['back', 'knee', 'pain', 'condition', 'disease', 'diabetes', 'blood pressure', 'cholesterol', 'arthritis', 'surgery'] },
    nutrition_diet:   { label: 'Nutrition / Diet',                keywords: ['eat', 'diet', 'nutrition', 'food', 'meal', 'calorie', 'macro', 'healthy eating'] },
    mental_health:    { label: 'Mental Health / Stress',          keywords: ['stress', 'mental', 'anxiety', 'sleep', 'mindful', 'meditation', 'wellness', 'peace'] },
  },
  wtpFactors: {
    price_affordability:    { label: 'Price / Affordability',          keywords: ['price', 'cost', 'afford', 'cheap', 'value', 'worth', 'money', 'budget', 'expensive'] },
    proven_results:         { label: 'Proven Results / Effectiveness', keywords: ['result', 'work', 'effective', 'proven', 'evidence', 'guarantee', 'success', 'outcome'] },
    personalization:        { label: 'Personalization / Customization',keywords: ['personal', 'individual', 'custom', 'tailor', 'adapt', 'my needs', 'specific to me'] },
    comprehensiveness:      { label: 'Comprehensive / All-in-One',     keywords: ['complete', 'comprehensive', 'everything', 'all-in-one', 'full', 'whole', 'entire'] },
    trial_guarantee:        { label: 'Trial / Money-Back Guarantee',   keywords: ['trial', 'free', 'money back', 'guarantee', 'refund', 'try', 'risk-free', 'sample'] },
    professional_guidance:  { label: 'Professional Guidance / Coaching',keywords: ['coach', 'trainer', 'professional', 'expert', 'guidance', 'support', 'accountability'] },
  },
  brandPerception: {
    unknown:          { label: 'Never Heard of It',        keywords: ['never', 'heard', 'nothing', 'idk', "don't know", 'no idea', 'no clue', "haven't heard", 'not sure', 'unfamiliar', '?'] },
    self_improvement: { label: 'Self-Improvement / Change',keywords: ['change', 'alter', 'improve', 'better', 'transform', 'new me', 'different'] },
    dna_genetics:     { label: 'DNA / Genetics',           keywords: ['dna', 'genetic', 'gene', 'genome'] },
    negative:         { label: 'Negative Associations',    keywords: ['scam', 'cult', 'fake', 'surgery', 'weird', 'suspicious', 'sketchy', 'gimmick'] },
    positive:         { label: 'Positive Associations',    keywords: ['helpful', 'innovative', 'interesting', 'cool', 'good', 'great', 'exciting', 'promising'] },
  },
};

function matchesTheme(text: string, keywords: string[]): boolean {
  const lower = text.toLowerCase();
  return keywords.some(k => lower.includes(k));
}

export function extractThemes(
  respondents: VerbatimRespondent[],
  field: ThemeField
): ThemeResult[] {
  const themeMap = THEME_KEYWORDS[field];
  const n = respondents.length;
  if (n === 0) return [];

  return Object.entries(themeMap).map(([theme, { label, keywords }]) => {
    const matches = respondents.filter(r => {
      const text = r[field] || '';
      return text.length > 0 && matchesTheme(text, keywords);
    });

    const quotes = matches
      .map(r => r[field])
      .filter(t => t.length > 10 && t.length < 250)
      .slice(0, 5);

    return {
      theme,
      label,
      count: matches.length,
      percentage: Math.round(matches.length / n * 100),
      sampleQuotes: quotes,
    };
  }).sort((a, b) => b.count - a.count);
}

export function getTopThemes(
  respondents: VerbatimRespondent[],
  field: ThemeField,
  limit: number = 3
): ThemeResult[] {
  return extractThemes(respondents, field).slice(0, limit);
}
